import {
  IonButton,
  IonContent,
  IonModal,
  IonSelect,
  IonSelectOption
} from '@ionic/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  ButtonContainer,
  ModalHeader,
  Padding,
  Spacer
} from '@/components/common';
import { Text } from '@/components/memo';
import { useDentist, useRequests } from '@/contexts';
import { useToast } from '@/hooks';
import { parseTime } from '@/utils/helpers/parseTime';
import { addMinutes, format, getDay, isAfter, isEqual } from 'date-fns';
import { arrowBackOutline } from 'ionicons/icons';

import './OffDuty.css';

interface OffDutyProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  presentingElement?: HTMLElement | undefined;
}

const OffDuty: React.FC<OffDutyProps> = ({
  isOpen,
  onClose,
  selectedDate,
  presentingElement
}) => {
  const { dentistHours } = useDentist();
  const { markAsOffDutyMutation } = useRequests();
  const displayToast = useToast();

  const modal = useRef<HTMLIonModalElement | null>(null);

  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, boolean>>({
    startTime: false,
    endTime: false
  });

  const getTimeOptions = (start: string, end: string) => {
    const startTime = parseTime(start);
    const endTime = parseTime(end);
    const calculatedTimeOptions = [];

    let currentTime = startTime;
    while (isAfter(endTime, currentTime)) {
      calculatedTimeOptions.push(format(currentTime, 'HH:mm'));
      currentTime = addMinutes(currentTime, 30);
    }

    return calculatedTimeOptions;
  };

  const timeOptions = useMemo(() => {
    if (dentistHours.length > 0) {
      const dayOfWeek = getDay(selectedDate);
      const hoursForDay = dentistHours.find((hours) => hours.day === dayOfWeek);
      if (hoursForDay && hoursForDay.start_time && hoursForDay.end_time) {
        return getTimeOptions(hoursForDay.start_time, hoursForDay.end_time);
      }
    }
    return Array.from({ length: 48 }, (_, i) => {
      const hours = String(Math.floor((i + 8) / 2) % 24).padStart(2, '0');
      const minutes = (i + 12) % 2 === 0 ? '00' : '30';
      return `${hours}:${minutes}`;
    });
  }, [dentistHours, selectedDate]);

  const reset = () => {
    setStartTime(null);
    setEndTime(null);
    setErrors({
      startTime: false,
      endTime: false
    });
  };

  const submit = useCallback(async () => {
    const newErrors = {
      startTime: !startTime,
      endTime: !endTime
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      displayToast({
        message: 'Please select both start and end times.',
        duration: 3000,
        position: 'bottom'
      });
      return;
    }

    // Validate that end time is after start time
    if (
      startTime &&
      endTime &&
      !isAfter(parseTime(endTime), parseTime(startTime))
    ) {
      displayToast({
        message: 'End time must be after start time.',
        duration: 3000,
        position: 'bottom'
      });
      return;
    }

    await markAsOffDutyMutation.mutateAsync({
      date: selectedDate,
      start_time: startTime!,
      end_time: endTime!
    });

    displayToast({
      message: `Off-duty period marked for ${format(selectedDate, 'MMMM d, yyyy')} from ${format(parseTime(startTime), 'h:mm a')} to ${format(parseTime(endTime), 'h:mm a')}.`,
      duration: 4000,
      position: 'bottom'
    });

    reset();
    onClose();
  }, [
    startTime,
    endTime,
    selectedDate,
    markAsOffDutyMutation,
    displayToast,
    onClose
  ]);

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  const copy = {
    subHeader: 'MARK AS OFF-DUTY',
    header: (
      <>
        Select <Text color="primary">time</Text>
      </>
    ),
    message: `This will prevent appointments from being booked on ${format(selectedDate, 'MMMM d, yyyy')}.`,
    startTimePlaceholder: 'Select start time',
    endTimePlaceholder: 'Select end time'
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => {
        reset();
        onClose();
      }}
      presentingElement={presentingElement}
      ref={modal}
    >
      <IonContent>
        <Padding withSafeArea={!presentingElement}>
          <div className="off-duty">
            <ModalHeader
              leftIcon={arrowBackOutline}
              onClickLeftIcon={() => {
                reset();
                modal.current?.dismiss();
              }}
            />
            <Spacer top={16} bottom={24}>
              <h4 className="off-duty__sub-header">{copy.subHeader}</h4>
              <h1 className="off-duty__header">{copy.header}</h1>
              <p className="off-duty__message">{copy.message}</p>
            </Spacer>

            <Spacer bottom={124}>
              <div className="off-duty__form">
                <div
                  className={`off-duty__input ${errors.startTime ? 'off-duty__input--error' : ''}`}
                >
                  <IonSelect
                    label="Start Time"
                    labelPlacement="stacked"
                    value={startTime}
                    compareWith={(a: string, b: string) =>
                      isEqual(parseTime(a), parseTime(b))
                    }
                    placeholder={copy.startTimePlaceholder}
                    onIonChange={(e: CustomEvent) => {
                      setStartTime(e.detail.value!);
                      setErrors((prev) => ({ ...prev, startTime: false }));
                    }}
                  >
                    {timeOptions.slice(0, -1).map((time) => (
                      <IonSelectOption key={time} value={time}>
                        {format(parseTime(time), 'h:mm a')}
                      </IonSelectOption>
                    ))}
                  </IonSelect>
                </div>
                <div
                  className={`off-duty__input ${errors.endTime ? 'off-duty__input--error' : ''}`}
                >
                  <IonSelect
                    label="End Time"
                    labelPlacement="stacked"
                    value={endTime}
                    compareWith={(a: string, b: string) =>
                      isEqual(parseTime(a), parseTime(b))
                    }
                    placeholder={copy.endTimePlaceholder}
                    onIonChange={(e: CustomEvent) => {
                      setEndTime(e.detail.value!);
                      setErrors((prev) => ({ ...prev, endTime: false }));
                    }}
                  >
                    {timeOptions
                      .filter(
                        (time) =>
                          !startTime ||
                          isAfter(parseTime(time), parseTime(startTime))
                      )
                      .map((time) => (
                        <IonSelectOption key={time} value={time}>
                          {format(parseTime(time), 'h:mm a')}
                        </IonSelectOption>
                      ))}
                  </IonSelect>
                </div>
              </div>
            </Spacer>

            <ButtonContainer>
              <IonButton
                onClick={submit}
                expand="block"
                fill="solid"
                size="default"
                color="primary"
                strong={true}
                disabled={markAsOffDutyMutation.isPending}
              >
                {markAsOffDutyMutation.isPending
                  ? 'Marking Off-Duty...'
                  : 'Mark as Off-Duty'}
              </IonButton>
            </ButtonContainer>
          </div>
        </Padding>
      </IonContent>
    </IonModal>
  );
};

export default OffDuty;
